import { modals } from "@mantine/modals";
import { toast } from "react-toastify";

import { MODAL_HANDLER } from "@/lib/Mantine";

import { nextCargoApi } from "../../baseApi";
import {
  DelayStatus,
  GetQueue,
  GetQueueActiveByShippingCompany,
  GetQueueDriverPosition,
  GetQueueNextDateByCompany,
  Queue,
  QueueByCompany,
  QueueCompany,
  QueueShippingCompanyPositions,
  SaveQueue,
  SaveQueueCloseById,
  SaveQueueDelete,
  SaveQueueStart,
  SaveQueueUpdate,
  SaveQueueUpdateDriver,
  ServiceStatus,
  ToggleQueueDelay
} from "./queue.types";
import { groupQueueByDays } from "./queue.utils";

export const queueApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    getQueueByCompany: build.query<QueueByCompany["result"], QueueByCompany["args"], QueueCompany[]>({
      query: ({ id }) => `/queue/company/${id}`,
      transformResponse: (response, meta, arg) => groupQueueByDays({ response, arg })
    }),
    getQueueActiveByCompany: build.query<QueueByCompany["result"], QueueByCompany["args"], QueueCompany[]>({
      query: ({ id }) => `/queue/active/company/${id}`,
      transformResponse: (response, meta, arg) => groupQueueByDays({ response, arg })
    }),
    getQueueActiveByShippingCompany: build.query<GetQueueActiveByShippingCompany["result"], GetQueueActiveByShippingCompany["args"]>({
      query: ({ id }) => `/queue/active/shipping-company/${id}`
    }),
    getQueueTotalsByCompany: build.query<ServiceStatus, { id: number }, ServiceStatus>({
      query: ({ id }) => `/queue/totals/company/${id}`
    }),
    getQueueDelayStatusByCompany: build.query<DelayStatus["result"], DelayStatus["args"]>({
      query: ({ id }) => `/queue/delayStatus/company/${id}`
    }),
    getQueueShippingCompanyPositions: build.query<QueueShippingCompanyPositions[], void>({
      query: () => "/queue/shipping-company/positions"
    }),
    getQueueByShippingCompany: build.query<Queue[], Pick<Queue, "id">>({
      query: ({ id }) => `/queue/shipping-company/${id}`
    }),
    getQueueDriverPosition: build.query<GetQueueDriverPosition["raw"], GetQueueDriverPosition["args"], GetQueueDriverPosition["result"]>({
      query: ({ id }) => `/queue/position/driver/${id}`,
      transformResponse: async (response) => {
        return {
          ...response,
          $isDriverActionsBlocked: false
        };
      }
    }),
    getQueue: build.query<GetQueue["result"], GetQueue["args"]>({
      query: ({ id }) => `/queue/${id}`
    }),
    getQueueNextDateByCompany: build.query<GetQueueNextDateByCompany["result"], GetQueueNextDateByCompany["args"]>({
      query: ({ id }) => `/queue/next-date/${id}`
    }),
    getQueueHasDriver: build.query<{ hasDriver: boolean }, { id: number }>({
      query: ({ id }) => `/queue/hasDriver/${id}`
    }),
    toggleQueueDelay: build.mutation<ToggleQueueDelay["result"], ToggleQueueDelay["args"]>({
      query: (body) => ({
        url: "/queue/delay",
        method: "PUT",
        body
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        const isDelaying = !args.time.startsWith("-");

        toast.promise(queryFulfilled, {
          pending: isDelaying ? "Registrando atraso..." : "Removendo atraso...",
          success: isDelaying ? "Atraso registrado com sucesso!" : "Atraso removido com sucesso!",
          error: isDelaying ? "Erro ao registrar o atraso!" : "Erro ao remover atraso!"
        });
      }
    }),
    saveQueueStart: build.mutation<SaveQueueStart["result"], SaveQueueStart["args"]>({
      query: (body) => ({
        url: "/queue/start",
        method: "PUT",
        body
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          pending: "Transferindo veículo para a fila...",
          success: "Sucesso ao transferir veículo para a fila!",
          error: "Erro ao transferir veículo para a fila!"
        });
      }
    }),
    saveQueueUpdateDriver: build.mutation<SaveQueueUpdateDriver["result"], SaveQueueUpdateDriver["args"]>({
      query: (body) => ({
        url: "/queue/update-driver",
        method: "PUT",
        body
      })
    }),
    saveQueueUpdate: build.mutation<SaveQueueUpdate["result"], SaveQueueUpdate["args"]>({
      query: (body) => ({
        url: "/queue/update",
        method: "PUT",
        body
      })
    }),
    saveQueueDelete: build.mutation<SaveQueueDelete["result"], SaveQueueDelete["args"]>({
      query: ({ id }) => ({
        url: `/queue/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          pending: "Cancelando...",
          success: "Cancelamento bem sucedido!"
        });

        try {
          await queryFulfilled;
        } catch (err: any) {
          const error = err?.error?.data?.message || "Erro desconhecido";
          toast.error(error);
          modals.closeAll();
        }
      }
    }),
    saveQueueCloseById: build.mutation<SaveQueueCloseById["result"], SaveQueueCloseById["args"]>({
      query: ({ id }) => ({
        url: `/queue/close/${id}`,
        method: "POST"
      })
    }),
    saveQueue: build.mutation<SaveQueue["result"], SaveQueue["args"]>({
      query: (body) => ({
        url: "/queue/save",
        method: "POST",
        body
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          pending: "Salvando...",
          success: "Salvo com sucesso!",
          //@ts-expect-error - Verificar tipo do error
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          error: (error: any) => {
            const err = error as { response?: { data?: { message?: string } } };
            const message = err.response?.data?.message;

            return `Erro ao salvar na fila: ${message || "Tente novamente mais tarde."}`;
          }
        });

        try {
          await queryFulfilled;
          MODAL_HANDLER.queue.successQueueStatus();
        } catch {
          // Do nothing
        }
      },
      transformErrorResponse: (err: unknown) => {
        const innerError = err as { response: { data: { message: string } } };
        const message = innerError.response?.data?.message;

        const error = {
          status: 1,
          message1: "",
          message2: "",
          message3: ""
        };

        if (message?.search("Você") !== -1) {
          error.message1 = message.slice(0, 50);
          error.message2 = message.slice(50, 80);
          error.message3 = message.slice(80, message.length);
          return error;
        }

        error.message1 = message || "";
        error.message2 = "";
        error.message3 = "";
        return error;
      }
    })
  })
});

queueApi.enhanceEndpoints({
  addTagTypes: ["Expedition", "QueueHasDriver"],
  endpoints: {
    getQueueByCompany: {
      providesTags: (result, error, { id }) => [{ type: "Queues", id }]
    },
    getQueueActiveByCompany: {
      providesTags: (result, error, { id }) => [{ type: "Queues", id }]
    },
    getQueueTotalsByCompany: {
      providesTags: (result, error, { id }) => [{ type: "Queues", id }]
    },
    getQueueDelayStatusByCompany: {
      providesTags: (result, error, { id }) => [{ type: "QueueDelayStatus", id }],
      extraOptions: {
        maxRetries: 0
      }
    },
    getQueueShippingCompanyPositions: {
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: "Queues" as const, id })), { type: "Queues", id: "LIST" }] : [{ type: "Queues", id: "LIST" }])
    },
    getQueueDriverPosition: {
      providesTags: (result, err, { id }) => [{ type: "DriverPosition", id }]
    },
    getQueue: {
      providesTags: (result, err, { id }) => [{ type: "Queue", id }]
    },
    getQueueActiveByShippingCompany: {
      providesTags: (result, err, { id }) => [{ type: "QueueByShippingCompany", id }]
    },
    getQueueByShippingCompany: {
      providesTags: (result, err, { id }) => [{ type: "QueueByShippingCompany", id }]
    },
    getQueueNextDateByCompany: {
      providesTags: (result, err, { id }) => [{ type: "QueueNextDateByCompany", id }]
    },
    getQueueHasDriver: {
      providesTags: (result, err, { id }) => [{ type: "QueueHasDriver", id }]
    },
    toggleQueueDelay: {
      invalidatesTags: ["Expedition", "SyncExpedition"]
    },
    saveQueueStart: {
      invalidatesTags: ["SyncExpedition", "Queues"]
    },
    saveQueueUpdateDriver: {
      invalidatesTags: ["SyncExpedition", "Queues"]
    },
    saveQueueUpdate: {
      invalidatesTags: ["SyncExpedition", "Queues"]
    },
    saveQueueCloseById: {
      invalidatesTags: ["SyncExpedition", "Queues"]
    },
    saveQueueDelete: {
      invalidatesTags: ["Queues", "Driver", "DriverPosition", "QueueHasDriver"]
    },
    saveQueue: {
      invalidatesTags: ["Queues", "Driver", "DriverPosition", "QueueHasDriver"]
    }
  }
});
